/**
 * Enhanced clipboard utility with mobile fallbacks
 * Provides robust copy-to-clipboard functionality that works across all devices
 */

/**
 * Detect if the current device is mobile
 * @returns boolean - true if device is mobile
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  return /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Copy text to clipboard with mobile fallbacks
 * @param text - The text to copy to clipboard
 * @param onSuccess - Optional callback when copy succeeds
 * @param onError - Optional callback when copy fails
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export async function copyToClipboard(
  text: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
): Promise<boolean> {
  const isMobile = isMobileDevice();
  
  try {
    // Try modern Clipboard API first (works in secure contexts)
    // On mobile, we still try this first, but it may require user gesture
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        onSuccess?.();
        return true;
      } catch (clipboardError) {
        // If Clipboard API fails (e.g., no user gesture on mobile), fall through to fallback
        console.warn("Clipboard API failed, trying fallback:", clipboardError);
        // Don't return here, continue to fallback
      }
    }
    
    // Fallback for mobile devices and older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // On mobile, we need to make the textarea temporarily visible and positioned correctly
    // This is necessary for focus() and select() to work properly
    if (isMobile) {
      // Position in viewport but make it very small and invisible
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2px";
      textArea.style.height = "2px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      textArea.style.zIndex = "-9999";
      textArea.style.overflow = "hidden";
    } else {
      // Desktop: completely hidden
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
    }
    
    textArea.setAttribute("readonly", "");
    textArea.setAttribute("aria-hidden", "true");
    textArea.setAttribute("tabindex", "-1");
    
    document.body.appendChild(textArea);
    
    // Focus and select the text
    // On mobile, we need to be more careful with focus
    try {
      textArea.focus();
      // Use setSelectionRange for better mobile support
      if (isMobile) {
        // On mobile, we need to select the text differently
        textArea.setSelectionRange(0, text.length);
      } else {
        textArea.select();
        textArea.setSelectionRange(0, text.length);
      }
      
      // Small delay on mobile to ensure selection is complete
      if (isMobile) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Use the deprecated but widely supported execCommand
      const successful = document.execCommand("copy");
      
      if (successful) {
        onSuccess?.();
        return true;
      } else {
        throw new Error("execCommand('copy') returned false");
      }
    } catch (execError) {
      console.warn("execCommand copy failed:", execError);
      
      // Last resort: try to show the text for manual copying
      onError?.("No se pudo copiar automáticamente. El enlace se mostrará para copia manual.");
      return false;
    } finally {
      // Always clean up the textarea safely
      try {
        if (textArea.parentNode) {
          document.body.removeChild(textArea);
        }
      } catch (cleanupError) {
        console.warn("Error cleaning up textarea:", cleanupError);
      }
    }
  } catch (error) {
    console.error("Clipboard copy error:", error);
    onError?.("Error al copiar el enlace");
    return false;
  }
}

/**
 * Copy text to clipboard with toast notifications
 * @param text - The text to copy to clipboard
 * @param showToast - Function to show toast notifications
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export async function copyToClipboardWithToast(
  text: string,
  showToast: (options: {
    title?: string;
    description: string;
    variant?: "default" | "destructive" | "success";
  }) => void
): Promise<boolean> {
  return copyToClipboard(
    text,
    () => {
      showToast({
        description: "Enlace copiado al portapapeles",
        variant: "success",
      });
    },
    (error) => {
      showToast({
        title: "No se pudo copiar automáticamente",
        description: error,
        variant: "default",
      });
    }
  );
}

/**
 * Check if clipboard API is available and secure
 * @returns boolean - true if clipboard API is available
 */
export function isClipboardAPIAvailable(): boolean {
  return !!(navigator.clipboard && window.isSecureContext);
}

/**
 * Crops away trailing blank rows/columns from a canvas. Needed because the
 * captured element is often a flex-1 container that stretches to fill its
 * parent's width/height, so the raw capture includes empty space below and
 * to the right of the real content.
 */
function trimCanvasWhitespace(
  canvas: HTMLCanvasElement,
  background: [number, number, number] = [255, 255, 255],
): HTMLCanvasElement {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const tolerance = 8;
  const xStep = Math.max(1, Math.floor(width / 400));
  const yStep = Math.max(1, Math.floor(height / 400));

  const isBackgroundPixel = (x: number, y: number) => {
    const idx = (y * width + x) * 4;
    const alpha = data[idx + 3];
    return (
      alpha === 0 ||
      (Math.abs(data[idx] - background[0]) <= tolerance &&
        Math.abs(data[idx + 1] - background[1]) <= tolerance &&
        Math.abs(data[idx + 2] - background[2]) <= tolerance)
    );
  };

  let lastContentRow = -1;
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x += xStep) {
      if (!isBackgroundPixel(x, y)) {
        lastContentRow = y;
        break;
      }
    }
    if (lastContentRow >= 0) break;
  }

  let lastContentColumn = -1;
  for (let x = width - 1; x >= 0; x--) {
    for (let y = 0; y < height; y += yStep) {
      if (!isBackgroundPixel(x, y)) {
        lastContentColumn = x;
        break;
      }
    }
    if (lastContentColumn >= 0) break;
  }

  const padding = 16;
  const trimmedHeight =
    lastContentRow < 0 || lastContentRow >= height - 1
      ? height
      : Math.min(height, lastContentRow + padding);
  const trimmedWidth =
    lastContentColumn < 0 || lastContentColumn >= width - 1
      ? width
      : Math.min(width, lastContentColumn + padding);

  if (trimmedHeight >= height && trimmedWidth >= width) return canvas;

  const trimmedCanvas = document.createElement("canvas");
  trimmedCanvas.width = trimmedWidth;
  trimmedCanvas.height = trimmedHeight;

  const trimmedCtx = trimmedCanvas.getContext("2d");
  trimmedCtx?.drawImage(
    canvas,
    0,
    0,
    trimmedWidth,
    trimmedHeight,
    0,
    0,
    trimmedWidth,
    trimmedHeight,
  );

  return trimmedCanvas;
}

/**
 * Copies an image blob (any format the browser can decode) to the clipboard.
 * The async Clipboard API only reliably accepts image/png, so the image is
 * re-encoded through a canvas first.
 * @param imageBlob - The image to copy
 * @throws When the browser can't write images to the clipboard
 */
export async function copyImageBlobToClipboard(
  imageBlob: Blob,
): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") {
    throw new Error("Clipboard image API not supported");
  }

  const bitmap = await createImageBitmap(imageBlob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error("Canvas 2D context not available");
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const pngBlob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  if (!pngBlob) throw new Error("Could not encode image as PNG");

  await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
}

/**
 * Renders a DOM element to a PNG and copies it to the clipboard as an image
 * (via the async Clipboard API). Falls back to downloading the PNG when the
 * browser can't write images to the clipboard (e.g. older Firefox).
 * @param element - The element to capture
 * @param fileNameForFallback - File name used when falling back to a download
 * @returns Promise<boolean> - true if the image was copied, false if it fell back to download
 */
export async function copyElementAsImage(
  element: HTMLElement,
  fileNameForFallback = "captura.png",
): Promise<boolean> {
  // Uses modern-screenshot (SVG foreignObject rendering) instead of
  // html2canvas: html2canvas reimplements text layout and mis-computes the
  // baseline, which shifted every label one line down in the capture.
  const { domToCanvas } = await import("modern-screenshot");

  // Make sure the web font (Figtree via next/font) is fully loaded before
  // capturing; capturing mid-swap makes the library fall back to a font with
  // different vertical metrics, which shifts the text.
  if (typeof document !== "undefined" && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // Non-fatal: proceed with whatever fonts are available.
    }
  }

  const rawCanvas = await domToCanvas(element, {
    backgroundColor: "#ffffff",
    scale: Math.min(window.devicePixelRatio || 1, 2),
  });
  const canvas = trimCanvasWhitespace(rawCanvas);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );

  if (!blob) throw new Error("No se pudo generar la imagen");

  if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      return true;
    } catch (clipboardError) {
      console.warn(
        "Clipboard image write failed, falling back to download:",
        clipboardError,
      );
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileNameForFallback;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return false;
}
